import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  AUTH_TOKEN,
  AUTH_TYPE,
  AUTH_ROLES,
  AUTH_ID,
  AUTH_NAME,
  AUTH_USER,
  AUTH_SESSION_EXPIRY,
  AUTH_DEVICE_TOKEN,
  ADMIN_TYPE,
  isAllowedAdminPanelLoginType,
} from 'constants/AuthConstant'
import AuthService from 'services/AuthService'
import { requestFCMToken } from 'auth/firebaseConfig'
// import FirebaseService from 'services/FirebaseService'
import userService from 'services/user'
import { normalizePermissionKey } from 'constants/RoutesKeys'
import { withNormalizedProfilePhoto } from 'utils/resolveProfilePhoto'
// import {
//   isSubAdminUserType,
//   isSubAdminEmailVerified,
// } from 'utils/isEmailVerifiedClaim'

const mapPermissionsToRoles = (permissions = []) => {
  const mappedRoles = {}
  permissions.forEach(({ key, ...role }) => {
    mappedRoles[normalizePermissionKey(key)] = role
  })
  return mappedRoles
}

/** Normalize API user for Redux + localStorage (API uses `type`, UI uses `userType`). */
export const normalizeUserFromLogin = (apiUser = {}) => {
  const userType = apiUser.type || apiUser.userType
  const nameParts = (apiUser.name || '').trim().split(/\s+/)
  return withNormalizedProfilePhoto({
    ...apiUser,
    userType,
    firstName: apiUser.firstName || nameParts[0] || '',
    lastName:
      apiUser.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''),
    phone: apiUser.phoneNo || apiUser.phone || apiUser.phoneNumber || '',
    permissions: apiUser.permissions ?? [],
  })
}

export const initialState = {
  loading: Boolean(localStorage.getItem(AUTH_TOKEN)),
  message: '',
  showMessage: false,
  authMessageDurationMs: 3000,
  redirect: '',
  user: JSON.parse(localStorage.getItem(AUTH_USER) || 'null'),
  authorized: null,
  token: localStorage.getItem(AUTH_TOKEN) || null,
  userId: localStorage.getItem(AUTH_ID) || null,
  type: localStorage.getItem(AUTH_TYPE) || null,
  userName: localStorage.getItem(AUTH_NAME) || null,
  roles: JSON.parse(localStorage.getItem(AUTH_ROLES) || 'null'),
  sessionExpiry: localStorage.getItem(AUTH_SESSION_EXPIRY) || null,
  constants: null,
  adminSettings: null,
}

export const tokenGenerator = async () => {
  try {
    const currentToken = await requestFCMToken()
    if (currentToken) {
      return currentToken
    }
  } catch (error) {
    console.error('Error getting FCM token:', error)
  }
}

const normalizeAuthMessagePayload = (payload) => {
  if (typeof payload === 'string') {
    return { message: payload, durationMs: 3000 }
  }
  return {
    message: payload?.message ?? '',
    durationMs: payload?.durationMs ?? 3000,
  }
}

export const signIn = createAsyncThunk(
  'auth/signIn',
  async (payload, { rejectWithValue }) => {
    const authinfo = payload.authinfo ?? payload.email
    const password = payload.password?.replace(/\s+/g, '')

    try {
      const currentToken = await tokenGenerator()
      const storedToken = localStorage.getItem(AUTH_DEVICE_TOKEN)
      const deviceToken = currentToken || storedToken || null

      if (currentToken) {
        localStorage.setItem(AUTH_DEVICE_TOKEN, currentToken)
      }

      const response = await AuthService.login({
        authinfo,
        password,
        deviceToken,
      })

      if (!response?.success) {
        return rejectWithValue(response?.message || 'Sign in failed')
      }

      const {
        sessionToken,
        sessionExpiry,
        user: apiUser = {},
      } = response.data ?? {}

      if (!sessionToken) {
        return rejectWithValue(response?.message || 'Sign in failed')
      }

      const userType = apiUser.type || apiUser.userType
      if (!isAllowedAdminPanelLoginType(userType)) {
        return rejectWithValue('Invalid login')
      }

      const mappedRoles = mapPermissionsToRoles(apiUser.permissions)
      const user = normalizeUserFromLogin(apiUser)

      localStorage.setItem(AUTH_TOKEN, sessionToken)
      localStorage.setItem(AUTH_TYPE, userType || '')
      localStorage.setItem(AUTH_ID, apiUser._id || '')
      localStorage.setItem(AUTH_NAME, apiUser.name || '')
      localStorage.setItem(AUTH_USER, JSON.stringify(user))
      localStorage.setItem(AUTH_ROLES, JSON.stringify(mappedRoles))
      if (sessionExpiry) {
        localStorage.setItem(AUTH_SESSION_EXPIRY, String(sessionExpiry))
      }

      return {
        token: sessionToken,
        sessionExpiry: sessionExpiry ? String(sessionExpiry) : null,
        type: userType,
        roles: mappedRoles,
        id: apiUser._id,
        username: apiUser.name || user.firstName,
        user,
      }

      // ------- Firebase login (legacy) — see multivendor-admin-react authSlice -----------
      // const response = await FirebaseService.signInEmailRequest(email, password)
      // if (response.user) {
      //   const token = response.user.refreshToken
      //   localStorage.setItem(AUTH_TOKEN, response.user.refreshToken)
      //   return token
      // } else {
      //   return rejectWithValue(response.message?.replace('Firebase: ', ''))
      // }
      // -------------------------------------------------------------------------------------
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          err?.toString?.() ||
          'Sign in failed'
      )
    }
  }
)

/**
 * On page reload, re-fetch the logged-in Sub Admin's details (incl.
 * `permissions`) from `GET /user/details` so role changes made by the
 * Super Admin take effect without a fresh login.
 *
 * Behaviour:
 *  - No token / invalid stored user → reject (App signs out).
 *  - Admin user → no permission matrix to refresh; restore from localStorage.
 *  - Sub Admin → call userService.getCurrentUserDetails(); on success
 *    re-map permissions → roles and persist. On any failure (service
 *    swallows errors and returns undefined) fall back to localStorage so
 *    transient network errors don't log the user out. 401 / 403 are
 *    already handled by FetchInterceptor (signOut + reload).
 */
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem(AUTH_TOKEN)
    if (!token) return rejectWithValue('no-token')

    const storedUser = JSON.parse(localStorage.getItem(AUTH_USER) || 'null')
    const storedType =
      storedUser?.userType ||
      storedUser?.type ||
      localStorage.getItem(AUTH_TYPE) ||
      ''

    if (!storedUser || !isAllowedAdminPanelLoginType(storedType)) {
      return rejectWithValue('invalid-session')
    }

    const fallback = {
      token,
      sessionExpiry: localStorage.getItem(AUTH_SESSION_EXPIRY),
      type: storedType,
      roles: JSON.parse(localStorage.getItem(AUTH_ROLES) || 'null'),
      id: localStorage.getItem(AUTH_ID),
      username: localStorage.getItem(AUTH_NAME),
      user: storedUser,
    }

    if (storedType === ADMIN_TYPE) {
      return fallback
    }

    const response = await userService.getCurrentUserDetails()
    // Tolerate response shapes: { data: user } | { data: { user } } | user
    const apiUser =
      response?.data?.user ??
      (Array.isArray(response?.data?.permissions) ? response.data : null) ??
      (Array.isArray(response?.permissions) ? response : null)

    if (!apiUser) {
      return fallback
    }

    const userType = apiUser.type || apiUser.userType || storedType
    if (!isAllowedAdminPanelLoginType(userType)) {
      return rejectWithValue('invalid-session')
    }

    const mappedRoles = mapPermissionsToRoles(apiUser.permissions)
    const user = normalizeUserFromLogin({ ...storedUser, ...apiUser })

    localStorage.setItem(AUTH_TYPE, userType || '')
    localStorage.setItem(AUTH_ID, apiUser._id || fallback.id || '')
    localStorage.setItem(AUTH_NAME, apiUser.name || fallback.username || '')
    localStorage.setItem(AUTH_USER, JSON.stringify(user))
    localStorage.setItem(AUTH_ROLES, JSON.stringify(mappedRoles))

    return {
      token,
      sessionExpiry: fallback.sessionExpiry,
      type: userType,
      roles: mappedRoles,
      id: apiUser._id ?? fallback.id,
      username: apiUser.name || user.firstName || fallback.username,
      user,
    }
  }
)

export const signOut = createAsyncThunk(
  'auth/signOut',
  async () => {
    // Call server logout while token still present, then always clear local auth.
    try {
      await AuthService.logout()
    } catch (error) {
      console.log(error, 'logout-api-err')
    }

    localStorage.removeItem(AUTH_TOKEN)
    localStorage.removeItem(AUTH_TYPE)
    localStorage.removeItem(AUTH_ROLES)
    localStorage.removeItem(AUTH_ID)
    localStorage.removeItem(AUTH_NAME)
    localStorage.removeItem(AUTH_USER)
    localStorage.removeItem(AUTH_SESSION_EXPIRY)
    return null
  }
)

export const signUp = createAsyncThunk(
  'auth/signUp',
  async (_payload, { rejectWithValue }) => {
    try {
      // const response = await FirebaseService.signUpEmailRequest(email, password)
      // if (response.user) {
      //   const token = response.user.refreshToken
      //   localStorage.setItem(AUTH_TOKEN, response.user.refreshToken)
      //   return token
      // } else {
      //   return rejectWithValue(response.message?.replace('Firebase: ', ''))
      // }
      return rejectWithValue('Sign up is not available')
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.toString?.() || 'Sign up failed'
      )
    }
  }
)

export const signInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      // const response = await FirebaseService.signInGoogleRequest()
      // if (response.user) {
      //   const token = response.user.refreshToken
      //   localStorage.setItem(AUTH_TOKEN, response.user.refreshToken)
      //   return token
      // } else {
      //   return rejectWithValue(response.message?.replace('Firebase: ', ''))
      // }
      return rejectWithValue('Google sign in is not available')
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.toString?.() || 'Google sign in failed'
      )
    }
  }
)

export const signInWithFacebook = createAsyncThunk(
  'auth/signInWithFacebook',
  async (_, { rejectWithValue }) => {
    try {
      // const response = await FirebaseService.signInFacebookRequest()
      // if (response.user) {
      //   const token = response.user.refreshToken
      //   localStorage.setItem(AUTH_TOKEN, response.user.refreshToken)
      //   return token
      // } else {
      //   return rejectWithValue(response.message?.replace('Firebase: ', ''))
      // }
      return rejectWithValue('Facebook sign in is not available')
    } catch (error) {
      return rejectWithValue(
        error?.message || error?.toString?.() || 'Facebook sign in failed'
      )
    }
  }
)

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false
      state.redirect = '/'
      state.authorized = true
      state.user = action.payload.user
      state.token = action.payload.token
      state.type = action.payload.type ?? state.type
      state.userId = action.payload.id ?? state.userId
      state.userName = action.payload.username ?? state.userName
      state.roles = action.payload.roles ?? state.roles
      state.sessionExpiry =
        action.payload.sessionExpiry ?? state.sessionExpiry
    },
    showAuthMessage: (state, action) => {
      const { message, durationMs } = normalizeAuthMessagePayload(action.payload)
      state.message = message
      state.showMessage = true
      state.loading = false
      state.authMessageDurationMs = durationMs
    },
    hideAuthMessage: (state) => {
      state.message = ''
      state.showMessage = false
      state.authMessageDurationMs = 3000
    },
    signOutSuccess: (state) => {
      state.token = null
      state.user = null
      state.authorized = null
      state.redirect = '/'
      state.loading = false
      state.type = null
      state.userName = null
      state.userId = null
      state.roles = null
      state.sessionExpiry = null
      state.constants = null
      state.message = ''
      state.showMessage = false
      state.authMessageDurationMs = 3000
    },
    showLoading: (state, action) => {
      state.loading =
        action.payload === null || action.payload === undefined
          ? true
          : action.payload
    },
    updateConstants: (state, action) => {
      state.constants = action.payload
    },
    saveAdminSettings: (state, action) => {
      state.adminSettings = action.payload
    },
    updateProfile: (state, action) => {
      const apiUser = action.payload.user
      state.user = normalizeUserFromLogin({ ...state.user, ...apiUser })
      state.userName = state.user?.name ?? apiUser.name ?? state.userName
    },
    updateAuthUser: (state, action) => {
      if (!state.user) return

      const nextUser = normalizeUserFromLogin({
        ...state.user,
        ...action.payload,
      })
      state.user = nextUser

      if (Array.isArray(action.payload?.permissions)) {
        state.roles = mapPermissionsToRoles(action.payload.permissions)
        localStorage.setItem(AUTH_USER, JSON.stringify(nextUser))
        localStorage.setItem(AUTH_ROLES, JSON.stringify(state.roles))
        return
      }

      localStorage.setItem(AUTH_USER, JSON.stringify(nextUser))
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false
        state.redirect = '/'
        state.token = action.payload?.token
        state.sessionExpiry = action.payload?.sessionExpiry ?? null
        state.authorized = true
        state.user = action.payload?.user
        state.type = action.payload?.type
        state.userId = action.payload?.id
        state.userName = action.payload?.username
        state.roles = action.payload?.roles
      })
      .addCase(signIn.rejected, (state, action) => {
        if (action.payload) {
          state.message = action.payload
          state.showMessage = true
        }
        state.loading = false
        state.authorized = null
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false
        state.authorized = true
        state.token = action.payload?.token
        state.sessionExpiry = action.payload?.sessionExpiry ?? null
        state.user = action.payload?.user
        state.type = action.payload?.type
        state.userId = action.payload?.id
        state.userName = action.payload?.username
        state.roles = action.payload?.roles
      })
      .addCase(restoreSession.rejected, (state) => {
        state.loading = false
      })
      .addCase(signOut.fulfilled, (state) => {
        state.token = null
        state.user = null
        state.authorized = null
        state.redirect = '/'
        state.loading = false
        state.type = null
        state.userName = null
        state.userId = null
        state.roles = null
        state.sessionExpiry = null
        state.constants = null
        state.message = ''
        state.showMessage = false
        state.authMessageDurationMs = 3000
      })
      .addCase(signOut.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
  },
})

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  updateConstants,
  saveAdminSettings,
  updateProfile,
  updateAuthUser,
} = authSlice.actions

export default authSlice.reducer
